import { useEffect, useState } from 'react'
import { Badge, Button, Input, Panel } from '../components/ui'

type ApiKey = {
  api_key_id: string
  label: string | null
  status: string
  scopes: string[]
  created_at: string
  last_used_at: string | null
}

export function ApiKeysRoute() {
  const [rows, setRows] = useState<ApiKey[]>([])
  const [label, setLabel] = useState('')
  const [secret, setSecret] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  async function load() {
    const response = await fetch('/api/admin/api-keys', { credentials: 'include', cache: 'no-store' })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Unable to load API keys')
    setRows(data.data || [])
    setLoading(false)
  }

  useEffect(() => {
    void load().catch((cause) => {
      setError(cause instanceof Error ? cause.message : 'Unable to load API keys')
      setLoading(false)
    })
  }, [])

  async function create() {
    setError('')
    const response = await fetch('/api/admin/api-keys', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ label }),
    })
    const data = await response.json()
    if (!response.ok) {
      setError(data.error || 'Unable to create API key')
      return
    }
    setSecret(data.secret)
    setLabel('')
    await load()
  }

  async function revoke(id: string) {
    const response = await fetch(`/api/admin/api-keys?id=${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' })
    if (!response.ok) setError('Unable to revoke API key')
    else await load()
  }

  return (
    <main className="mx-auto max-w-7xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-6">
      <div><p className="text-xs text-ink-muted">Tenant-scoped credentials</p><h2 className="mt-1 text-xl font-semibold text-ink">API Keys</h2></div>
      <Panel title="Create API key">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input className="min-w-0 flex-1" aria-label="Key label" placeholder="Label" value={label} onChange={(event) => setLabel(event.target.value)} />
          <Button variant="primary" onClick={() => void create()}>Create key</Button>
        </div>
        {secret && <p className="mt-3 break-all rounded border border-accent-warn/40 bg-accent-warn/10 p-3 font-mono text-xs leading-5 text-accent-warn">Copy this secret now. It will not be shown again: {secret}</p>}
        {error && <p role="alert" className="mt-3 text-xs text-accent-danger">{error}</p>}
      </Panel>
      <Panel title="Existing keys">
        {loading ? <p className="text-sm text-ink-muted">Loading…</p> : rows.length === 0 ? <p className="text-sm text-ink-muted">No API keys.</p> : (
          <>
            <div className="grid gap-2 sm:hidden">
              {rows.map((row) => <div key={row.api_key_id} className="rounded-lg border border-panel-border bg-panel-muted p-3">
                <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="truncate font-medium text-ink">{row.label || 'Unlabelled'}</div><div className="mt-1 text-xs text-ink-muted">Created {row.created_at}</div></div><Badge tone={row.status === 'active' ? 'ok' : 'muted'}>{row.status}</Badge></div>
                {row.status === 'active' && <Button className="mt-3 w-full" variant="danger" onClick={() => void revoke(row.api_key_id)}>Revoke key</Button>}
              </div>)}
            </div>
            <div className="hidden overflow-x-auto sm:block"><table className="w-full text-left text-sm"><thead><tr className="border-b border-panel-border text-xs text-ink-faint"><th className="px-3 py-2">Label</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Created</th><th className="px-3 py-2" /></tr></thead><tbody className="divide-y divide-panel-border">{rows.map((row) => <tr key={row.api_key_id}><td className="px-3 py-3">{row.label || 'Unlabelled'}</td><td className="px-3 py-3"><Badge tone={row.status === 'active' ? 'ok' : 'muted'}>{row.status}</Badge></td><td className="px-3 py-3 text-ink-muted">{row.created_at}</td><td className="px-3 py-3 text-right">{row.status === 'active' && <Button variant="danger" onClick={() => void revoke(row.api_key_id)}>Revoke</Button>}</td></tr>)}</tbody></table></div>
          </>
        )}
      </Panel>
    </main>
  )
}
